import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import type { ShopDetailNavState } from "../../../utils/shopListReturn";
import Navbar from "../../Menu/Navbar";
import Preloader from "../../../Components/Preloader";
import Footer from "../Sections/Footer";
import Slider from "react-slick";
import RateAndReview from "../../../Components/biblio/RateAndReview";
import AddReview from "../../../Components/biblio/AddReview";
import { AppDispatch, RootState } from "../../../apis/store";
import { useDispatch, useSelector } from "react-redux";
import { toGetDetails, toGetServiceProductVariants } from "../../../apis/actions/service.actions";
import { useCookies } from "react-cookie";
import QuestionAnswers from "../../../Components/biblio/QuestionAnswers";
import { useTranslation } from "react-i18next";
import { Breadcrumb, Spin } from "antd";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import { HiOutlineStar } from "react-icons/hi2";
import {
  toAddOrRemoveProduct,
  toAddOrRemoveService,
} from "../../../apis/slices/globalSlice";
import { FaCube } from "react-icons/fa6";
import { TbCubeOff } from "react-icons/tb";
import { FaShoppingCart } from "react-icons/fa";
import { BsCartPlusFill } from "react-icons/bs";
import { BsPlayFill, BsZoomIn } from "react-icons/bs";
import { buildMediaSlides } from "../../../utils/mediaSlides";
import { IoClose } from "react-icons/io5";
import useIsConnected from "../../../utils/IsConnected";
import { signalCartAddedFeedback } from "../../../utils/cartAddedFeedback";
import {
  isValidServiceImage,
  resolveServiceImageSrc,
  SERVICE_IMAGE_FALLBACK,
} from "../../../utils/serviceImage";
import ChooseVehicleModal from "../../../Components/vehicule/ChooseVehicleModal";
import DetailCollapseSection from "../../../Components/biblio/DetailCollapseSection";
import ServiceVariantSelector from "../../../Components/shopDetail/ServiceVariantSelector";
import {
  buildCartServiceFromVariant,
  findVariantBySelections,
  getDefaultVariantSelections,
  getSelectableAttributeKeys,
  getVisibleVariantAttributes,
  parseServiceProductVariantsPayload,
  resolveSelectionsAfterChange,
  type ServiceProductVariants,
  type ServiceVariantSelections,
} from "../../../utils/serviceVariants";
import {
  getServiceCatalogDiscount,
  getServiceCatalogPrice,
  getServiceWorkHours,
} from "../../../utils/serviceCatalog";
import WindowTintSelector from "../../../Components/shopDetail/WindowTintSelector";
import { getProductStockStatus } from "../../../utils/productStock";
import {
  buildTintDetails,
  getTintOptions,
  isWindowTintingService,
  pruneTintSelections,
  type TintSelections,
} from "../../../utils/windowTinting";

const Detail = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const [cookies] = useCookies(["apiKey"]);
  const servicesState = useSelector((state: RootState) => state.services);
  const list = servicesState.details.list;
  const detailsStatus = servicesState.details.status;
  const serviceVariantsState = servicesState.serviceVariants;
  const isVariantsLoading = serviceVariantsState?.status === "loading";
  const { products, vehicle } = useSelector((state: RootState) => state.global);
  const addShopLoading = useSelector(
    (state: RootState) => state?.services?.AddShop?.loading ?? false
  );
  const navState = (location.state ?? {}) as ShopDetailNavState;
  const [state, setState] = useState<any>(() => navState);
  const returnTo = navState.returnTo;
  const [nb, setNb] = useState<number>(1);
  const [chooseVehicleOpen, setChooseVehicleOpen] = useState(false);
  const [pendingServiceAddAfterVehicle, setPendingServiceAddAfterVehicle] =
    useState(false);
  const [activeSlide, setActiveSlide] = useState(0);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [openSection, setOpenSection] = useState<string | null>("overview");
  const [selectedAttributes, setSelectedAttributes] =
    useState<ServiceVariantSelections>({});
  const [tintSelections, setTintSelections] = useState<TintSelections>({});
  const isConnected = useIsConnected();
  const isRtl = i18n.language === "ar";
  const isService = state?.type === "service";
  const isLoading = isService
    ? serviceVariantsState?.status === "loading" ||
      serviceVariantsState?.status === "idle"
    : detailsStatus === "loading";
  const shopPath = isService ? "/services" : "/products";
  const shopLabel = isService ? t("menu.services") : t("menu.products");

  const variantDataFromList = useMemo(() => {
    if (!isService || !list || (list as any)?.id == null) return null;
    return parseServiceProductVariantsPayload([list], Number((list as any).id));
  }, [isService, list]);

  const variantData = serviceVariantsState?.data ?? variantDataFromList ?? null;

  const selectableKeys = useMemo(
    () =>
      getSelectableAttributeKeys(
        variantData?.variant_attributes ??
          (list as any)?.variant_attributes
      ),
    [variantData?.variant_attributes, list]
  );

  const catalogVariants = useMemo(
    () =>
      variantData?.variants ??
      (Array.isArray((list as any)?.variants) ? (list as any).variants : []),
    [variantData?.variants, list]
  );

  const variantSelectionSource = useMemo((): ServiceProductVariants | null => {
    if (variantData) return variantData;
    if (!list || selectableKeys.length === 0 || catalogVariants.length === 0) {
      return null;
    }
    return {
      ...(list as ServiceProductVariants),
      variant_attributes:
        (list as any)?.variant_attributes ?? ({} as ServiceProductVariants["variant_attributes"]),
      variants: catalogVariants,
    };
  }, [catalogVariants, list, selectableKeys.length, variantData]);

  const toggleSection = useCallback((sectionId: string) => {
    setOpenSection((prev) => (prev === sectionId ? null : sectionId));
  }, []);

  const handleReturnToShop = useCallback(
    (e?: React.MouseEvent) => {
      e?.preventDefault();
      if (returnTo) {
        navigate(-1);
      } else {
        navigate(shopPath);
      }
    },
    [navigate, returnTo, shopPath]
  );

  useEffect(() => {
    if (location.state) {
      setState(location.state);
    } else {
      navigate("/");
    }
  }, [location.state, navigate]);

  useEffect(() => {
    if (!state?.id) return;

    if (isService) {
      dispatch(
        toGetServiceProductVariants({
          serviceId: Number(state.id),
          vehicleId: vehicle?.id,
          lang: i18n.language,
        })
      );
      return;
    }

    dispatch(toGetDetails({ id: state.id, lang: i18n.language }));
  }, [
    dispatch,
    i18n.language,
    isService,
    state?.id,
    vehicle?.id,
  ]);

  const refreshServiceDetail = useCallback(() => {
    if (!isService || !state?.id) return;
    dispatch(
      toGetServiceProductVariants({
        serviceId: Number(state.id),
        vehicleId: vehicle?.id,
        lang: i18n.language,
        forceRefresh: true,
      })
    );
  }, [dispatch, i18n.language, isService, state?.id, vehicle?.id]);

  const variantFetchKey = `${serviceVariantsState?.serviceId ?? ""}:${serviceVariantsState?.vehicleId ?? ""}`;

  useEffect(() => {
    if (variantSelectionSource) {
      setSelectedAttributes(
        getDefaultVariantSelections(variantSelectionSource)
      );
      return;
    }
    setSelectedAttributes({});
  }, [variantSelectionSource, variantFetchKey]);

  const selectedVariant = useMemo(
    () =>
      findVariantBySelections(
        catalogVariants,
        selectedAttributes,
        selectableKeys
      ),
    [catalogVariants, selectedAttributes, selectableKeys]
  );

  const activeListPrice = selectedVariant
    ? getServiceCatalogPrice(selectedVariant)
    : getServiceCatalogPrice(list);
  const activeDiscountPrice = selectedVariant
    ? getServiceCatalogDiscount(selectedVariant)
    : getServiceCatalogDiscount(list);
  const activeDuration = selectedVariant
    ? getServiceWorkHours(selectedVariant)
    : getServiceWorkHours(list);
  const activeWarranty = selectedVariant?.warranty ?? list?.warranty;
  const activeSalesCount =
    selectedVariant?.total_sales_count ?? list?.total_sales_count;
  const validSalesCount = Number(activeSalesCount);
  const showSalesCount = Number.isSafeInteger(validSalesCount) && validSalesCount > 0;
  const showReviewRating =
    Number(list?.reviews_count) > 0 && Number(list?.reviews_rate) > 0;
  const productStock = isService ? null : getProductStockStatus(list);

  // Tint options live on both the template and its variants; either will do.
  const tintSource = variantData ?? list;
  const isTintService = isService && isWindowTintingService(tintSource);
  const tintOptions = useMemo(() => getTintOptions(tintSource), [tintSource]);
  const tintDetails = useMemo(
    () => buildTintDetails(tintSelections),
    [tintSelections]
  );
  const isTintIncomplete = isTintService && tintDetails.length === 0;

  const serviceForCart = useMemo(() => {
    if (!list || (list as any)?.id == null) return list;

    let base = list as Record<string, unknown>;
    if (selectedVariant) {
      base = buildCartServiceFromVariant(base, selectedVariant);
    } else {
      const embeddedVariants = (list as any)?.variants;
      if (Array.isArray(embeddedVariants) && embeddedVariants.length === 1) {
        base = buildCartServiceFromVariant(base, embeddedVariants[0]);
      }
    }

    // Travels with the cart line so `/v1/shop` receives it at checkout.
    return isTintService ? { ...base, tint_details: tintDetails } : base;
  }, [isTintService, list, selectedVariant, tintDetails]);

  // Car size is derived from the customer's vehicle, so it is not offered here.
  const variantAttributesForUi = useMemo(
    () =>
      getVisibleVariantAttributes(
        variantData?.variant_attributes ?? (list as any)?.variant_attributes
      ),
    [variantData?.variant_attributes, list]
  );

  const hasVariantSelector = Boolean(
    isService &&
      Object.keys(variantAttributesForUi).length > 0 &&
      catalogVariants.length > 1
  );

  useEffect(() => {
    if (!isTintService) {
      setTintSelections((prev) => (Object.keys(prev).length ? {} : prev));
      return;
    }
    setTintSelections((prev) =>
      pruneTintSelections(prev, tintOptions.glassTypes)
    );
  }, [isTintService, tintOptions.glassTypes]);

  const handleTintChange = useCallback(
    (glassTypeId: number, tintPercentageId: number | null) => {
      setTintSelections((prev) => {
        const next = { ...prev };
        if (tintPercentageId == null) {
          delete next[glassTypeId];
        } else {
          next[glassTypeId] = tintPercentageId;
        }
        return next;
      });
    },
    []
  );

  const handleVariantChange = useCallback(
    (attributeKey: string, valueId: number) => {
      if (!catalogVariants.length) return;
      setSelectedAttributes((prev) =>
        resolveSelectionsAfterChange(
          catalogVariants,
          prev,
          attributeKey,
          valueId,
          selectableKeys
        )
      );
    },
    [catalogVariants, selectableKeys]
  );

  const realImages = useMemo(() => {
    const extras = Array.isArray(list?.image_ids)
      ? list.image_ids
          .map((img: any) => img?.datas)
          .filter((url: unknown) =>
            isService ? isValidServiceImage(url) : Boolean(url)
          )
      : [];

    if (isService) {
      const main = isValidServiceImage(list?.image_1920)
        ? resolveServiceImageSrc(list.image_1920)
        : null;
      if (main) return [main, ...extras.filter((url: string) => url !== main)];
      if (extras.length > 0) return extras;
      return [SERVICE_IMAGE_FALLBACK];
    }

    return [list?.image_1920, ...extras].filter(Boolean) as string[];
  }, [isService, list?.image_1920, list?.image_ids]);

  const hasDiscount = useMemo(() => {
    const discount = activeDiscountPrice;
    const price = activeListPrice;
    return (
      discount != null &&
      discount > 0 &&
      price != null &&
      discount !== price
    );
  }, [activeDiscountPrice, activeListPrice]);

  useEffect(() => {
    const index = products.findIndex((el: any) => el.id === state?.id);
    if (index !== -1) {
      setNb(products[index].qty);
    }
  }, [products, state?.id]);

  useEffect(() => {
    if (!pendingServiceAddAfterVehicle || !vehicle) return;
    if (!isService) return;
    if (list == null || (list as any)?.id == null) return;
    if (hasVariantSelector && !selectedVariant) return;
    if (isTintIncomplete) return;
    setPendingServiceAddAfterVehicle(false);
    signalCartAddedFeedback();
    dispatch(toAddOrRemoveService(serviceForCart));
  }, [
    dispatch,
    hasVariantSelector,
    isService,
    isTintIncomplete,
    list,
    pendingServiceAddAfterVehicle,
    selectedVariant,
    serviceForCart,
    vehicle,
  ]);

  const closeChooseVehicleModal = useCallback(() => {
    setChooseVehicleOpen(false);
  }, []);

  const onVehicleChosenForServiceDetail = useCallback(() => {
    setPendingServiceAddAfterVehicle(true);
  }, []);

  const handleAddToCart = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!isConnected) {
        navigate("/login");
        return;
      }
      if (isService && !vehicle?.id) {
        setPendingServiceAddAfterVehicle(true);
        setChooseVehicleOpen(true);
        return;
      }
      signalCartAddedFeedback();
      if (isService) {
        dispatch(toAddOrRemoveService(serviceForCart));
      } else {
        dispatch(toAddOrRemoveProduct({ ...list, qty: nb }));
      }
    },
    [dispatch, isConnected, isService, list, nb, navigate, serviceForCart, vehicle]
  );

  const displayTitle = list?.name ?? selectedVariant?.name ?? state?.name ?? "…";
  const slides = useMemo(
    () => buildMediaSlides(realImages, list?.video_ids),
    [realImages, list?.video_ids]
  );
  const productName = displayTitle;

  useEffect(() => {
    if (lightboxIndex == null) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") setLightboxIndex(null);
      if (slides.length < 2) return;
      if (event.key === "ArrowLeft") {
        setLightboxIndex((current) =>
          current == null ? null : (current - 1 + slides.length) % slides.length
        );
      }
      if (event.key === "ArrowRight") {
        setLightboxIndex((current) =>
          current == null ? null : (current + 1) % slides.length
        );
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [slides.length, lightboxIndex]);

  const sliderSettings = useMemo(
    () => ({
      customPaging: (i: number) => (
        <button
          type="button"
          className={`ac-shop-detail__thumb-btn${
            i === activeSlide ? " ac-shop-detail__thumb-btn--active" : ""
          }`}
          aria-label={`${i + 1} / ${slides.length}`}
          aria-current={i === activeSlide ? "true" : undefined}
        >
          {slides[i]?.video ? (
            <span className="ac-shop-detail__thumb-video">
              <video src={slides[i].src} muted preload="metadata" playsInline />
              <BsPlayFill aria-hidden />
            </span>
          ) : (
            <img
              src={slides[i]?.src}
              alt=""
              width={64}
              height={64}
              decoding="async"
            />
          )}
        </button>
      ),
      dots: true,
      dotsClass: "ac--slick-dots ac-shop-detail__thumbs slick-thumb",
      infinite: slides.length > 1,
      speed: 450,
      slidesToShow: 1,
      slidesToScroll: 1,
      arrows: false,
      rtl: isRtl,
      adaptiveHeight: false,
      afterChange: (current: number) => {
        setActiveSlide(current);
        // ponytail: swiping away shouldn't leave audio playing; one query beats a ref per slide.
        document
          .querySelectorAll<HTMLVideoElement>(".ac-shop-detail__slide video")
          .forEach((el) => el.pause());
      },
    }),
    [activeSlide, slides, isRtl]
  );

  const breadcrumbSeparator =
    isRtl ? <IoIosArrowBack /> : <IoIosArrowForward />;

  const renderPurchaseCta = () => {
    if (!vehicle && isService && isConnected) {
      return (
        <button
          type="button"
          className="ac-shop-detail__cta btn"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setChooseVehicleOpen(true);
          }}
        >
          <BsCartPlusFill size={16} aria-hidden />
          {t("services.chooseVehicle.cta")}
        </button>
      );
    }

    return (
      <>
        <button
          type="button"
          className="ac-shop-detail__cta btn"
          disabled={
            addShopLoading ||
            list == null ||
            (list as any)?.id == null ||
            (hasVariantSelector && !selectedVariant) ||
            isTintIncomplete
          }
          onClick={handleAddToCart}
        >
          {addShopLoading ? <Spin size="small" /> : <BsCartPlusFill size={16} aria-hidden />}
          {t("services.cart")}
        </button>
        {isTintIncomplete ? (
          <p className="ac-shop-detail__cta-note">
            {t("services.tint.required")}
          </p>
        ) : null}
      </>
    );
  };

  return (
    <>
      <Navbar type="internal" />
      <Preloader />
      <main className="main ac-shop-detail">
        <section className="box-section background-body ac-shop-detail__breadcrumb">
          <div className="container">
            <Breadcrumb
              className={`${isRtl ? "ac--font-expo" : ""} ac--details-breadcrumb`}
              separator={breadcrumbSeparator}
              items={[
                { title: <span>{t("menu.home")}</span>, href: "/" },
                returnTo
                  ? {
                      title: <span>{shopLabel}</span>,
                      onClick: (e) => {
                        e.preventDefault();
                        handleReturnToShop();
                      },
                    }
                  : { title: <span>{shopLabel}</span>, href: shopPath },
                { title: displayTitle },
              ]}
            />
          </div>
        </section>

        <section className="section-box background-body ac-shop-detail__hero">
          <div className="container">
            <button
              type="button"
              className={`ac-shop-detail__back${isRtl ? " ac--font-expo" : ""}`}
              onClick={handleReturnToShop}
            >
              {isRtl ? <IoIosArrowForward aria-hidden /> : <IoIosArrowBack aria-hidden />}
              {t("services.detail.back", { type: shopLabel })}
            </button>

            <div className="row g-4 g-lg-5 align-items-start">
              <div className="col-lg-6">
                <div className="ac-shop-detail__gallery">
                  <div className="ac-shop-detail__gallery-stage">
                  {isLoading ? (
                    <div className="ac-shop-detail__gallery-placeholder">
                      <Spin size="large" />
                    </div>
                  ) : slides.length > 0 ? (
                    <>
                        <div className="ac-shop-detail__slider-wrap ac--slider-container">
                      <Slider {...sliderSettings}>
                        {slides.map((slide, index: number) => (
                          <div key={`${slide.src}-${index}`}>
                            {slide.video ? (
                              <div
                                className="ac-shop-detail__slide ac-shop-detail__slide--video"
                                // Keep control clicks away from the slider's drag handler.
                                onClick={(event) => event.stopPropagation()}
                              >
                                <video
                                  src={slide.src}
                                  controls
                                  preload="metadata"
                                  playsInline
                                  aria-label={slide.name ?? productName}
                                />
                              </div>
                            ) : (
                              <div
                                className={`ac-shop-detail__slide${
                                  isService ? " ac-shop-detail__slide--service" : " ac-shop-detail__slide--product"
                                }`}
                                role="button"
                                tabIndex={0}
                                aria-label={t("services.detail.zoomImage")}
                                onClick={() => setLightboxIndex(index)}
                                onKeyDown={(event) => {
                                  if (event.key === "Enter" || event.key === " ") {
                                    event.preventDefault();
                                    setLightboxIndex(index);
                                  }
                                }}
                              >
                                <img
                                  src={slide.src}
                                  alt={
                                    productName
                                      ? `${productName} — ${index + 1}`
                                      : ""
                                  }
                                  className={
                                    isService && slide.src === SERVICE_IMAGE_FALLBACK
                                      ? "ac-service-thumbnail--fallback"
                                      : undefined
                                  }
                                  loading={index === 0 ? "eager" : "lazy"}
                                  decoding="async"
                                  draggable={false}
                                />
                                <span className="ac-shop-detail__zoom-hint" aria-hidden>
                                  <BsZoomIn />
                                  {t("services.detail.zoom")}
                                </span>
                              </div>
                            )}
                          </div>
                        ))}
                      </Slider>
                        </div>
                        {slides.length > 1 && (
                          <span
                            className="ac-shop-detail__slide-counter"
                            aria-live="polite"
                          >
                            {activeSlide + 1} / {slides.length}
                          </span>
                        )}
                      </>
                  ) : (
                    <div className="ac-shop-detail__gallery-placeholder">
                      <div className="ac-shop-detail__slide">
                        <img
                          src={
                            isService
                              ? SERVICE_IMAGE_FALLBACK
                              : "assets/imgs/shop/shop-list/product2.png"
                          }
                          alt={productName}
                          className={
                            isService ? "ac-service-thumbnail--fallback" : undefined
                          }
                          loading="eager"
                          decoding="async"
                        />
                      </div>
                    </div>
                  )}
                  </div>
                </div>
              </div>

              <div className="col-lg-6">
                <div className={`ac-shop-detail__panel${isRtl ? " ac--font-expo" : ""}`}>
                  <div className="ac-shop-detail__meta">
                    {showReviewRating && (
                      <span className="ac-shop-detail__chip ac-shop-detail__chip--rating">
                        <HiOutlineStar size={14} aria-hidden />
                        {list.reviews_rate}
                        <span className="text-lowercase fw-normal">
                          ({list?.reviews_count ?? 0} {t("menu.reviews")})
                        </span>
                      </span>
                    )}
                    {!isService && productStock !== null && <span
                      className={`ac-shop-detail__chip ${
                        productStock
                          ? "ac-shop-detail__chip--stock-in"
                          : "ac-shop-detail__chip--stock-out"
                      }`}
                    >
                      {productStock ? (
                        <FaCube size={13} color="#5edd5b" aria-hidden />
                      ) : (
                        <TbCubeOff size={13} color="#990033" aria-hidden />
                      )}
                      {productStock ? t("services.stock.in") : t("services.stock.out")}
                    </span>}
                    {showSalesCount && <span className="ac-shop-detail__chip ac-shop-detail__chip--sales">
                      <FaShoppingCart size={12} aria-hidden />
                      {validSalesCount} {t("menu.sold")}
                    </span>}
                  </div>

                  <h1 className="ac-shop-detail__title">
                    {isLoading ? "…" : displayTitle}
                  </h1>

                  <div className="ac-shop-detail__price-block">
                    {hasDiscount ? (
                      <>
                        <p className="ac-shop-detail__price-current icon-saudi_riyal mb-0">
                          {activeDiscountPrice}
                        </p>
                        <p className="ac-shop-detail__price-was icon-saudi_riyal mb-0">
                          {activeListPrice}
                        </p>
                      </>
                    ) : (
                      <p className="ac-shop-detail__price-current icon-saudi_riyal mb-0">
                        {activeListPrice ?? state?.price}
                      </p>
                    )}
                    <p className="ac-shop-detail__vat mb-0">
                      ({t("profile.order.cart.vat")})
                    </p>
                  </div>

                  {isService && (
                    <div className="ac-shop-detail__installments">
                      <span>{t("services.installmentsAvailability")}</span>
                      <span className="ac-shop-detail__installment-providers" aria-hidden="true">
                        <img src={`${process.env.PUBLIC_URL ?? ""}/assets/imgs/bnpl/tabby-logo.png`} alt="" loading="lazy" />
                        <img src={`${process.env.PUBLIC_URL ?? ""}/assets/imgs/bnpl/tamara-logo.png`} alt="" loading="lazy" />
                      </span>
                    </div>
                  )}

                  {isService && isVariantsLoading ? (
                    <div className="ac-shop-detail__variants ac-shop-detail__variants--loading">
                      <Spin size="small" />
                      <span>{t("services.detail.variantsLoading")}</span>
                    </div>
                  ) : null}

                  {hasVariantSelector ? (
                    <ServiceVariantSelector
                      className="ac-shop-detail__variants"
                      variantAttributes={variantAttributesForUi}
                      variants={catalogVariants}
                      selectableKeys={selectableKeys}
                      selectedByAttribute={selectedAttributes}
                      onChange={handleVariantChange}
                    />
                  ) : null}

                  {isTintService ? (
                    <>
                      <WindowTintSelector
                        className="ac-shop-detail__tint"
                        glassTypes={tintOptions.glassTypes}
                        tintPercentages={tintOptions.tintPercentages}
                        selections={tintSelections}
                        onChange={handleTintChange}
                      />
                      <p className="ac-shop-detail__tint-disclaimer">
                        {t("services.tint.disclaimer")}
                      </p>
                    </>
                  ) : null}

                  {isService &&
                    (Number(activeWarranty) > 0 || Number(activeDuration) > 0) && (
                      <div className="ac-shop-detail__service-meta">
                        {Number(activeWarranty) > 0 && (
                          <div className="ac-shop-detail__service-stat">
                            <span className="ac-shop-detail__service-stat-value">
                              {activeWarranty}
                            </span>
                            <span className="ac-shop-detail__service-stat-label">
                              {t("services.years")} · {t("services.garranty")}
                            </span>
                          </div>
                        )}
                        {Number(activeDuration) > 0 && (
                          <div className="ac-shop-detail__service-stat">
                            <span className="ac-shop-detail__service-stat-value">
                              {activeDuration}
                              <span className="text-sm-bold">{t("services.hr")}</span>
                            </span>
                            <span className="ac-shop-detail__service-stat-label">
                              {t("services.workingHours")}
                            </span>
                          </div>
                        )}
                      </div>
                    )}

                  {list?.description_website &&
                  typeof list.description_website === "string" ? (
                    <div
                      className="ac-shop-detail__description list-style-disc"
                      dangerouslySetInnerHTML={{
                        __html: list.description_website,
                      }}
                    />
                  ) : null}

                  <div className="ac-shop-detail__purchase">
                    <div className="ac-shop-detail__purchase-row">
                      {!isService && (
                        <div className="ac-shop-detail__qty" role="group" aria-label={t("services.detail.quantity")}>
                          <button
                            type="button"
                            className="ac-shop-detail__qty-btn"
                            aria-label="-"
                            disabled={nb <= 1}
                            onClick={() => setNb((n) => (n > 1 ? n - 1 : 1))}
                          >
                            −
                          </button>
                          <input
                            type="text"
                            readOnly
                            className="ac-shop-detail__qty-val"
                            value={nb}
                            aria-live="polite"
                          />
                          <button
                            type="button"
                            className="ac-shop-detail__qty-btn"
                            aria-label="+"
                            onClick={() => setNb((n) => n + 1)}
                          >
                            +
                          </button>
                        </div>
                      )}
                      {renderPurchaseCta()}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="box-section background-body ac-shop-detail__sections pt-0">
          <div className="container">
            <header className="ac-shop-detail__sections-head">
              <h2 className={`ac-shop-detail__sections-title${isRtl ? " ac--font-expo" : ""}`}>
                {t("services.detail.sectionsTitle")}
              </h2>
              <p className="ac-shop-detail__sections-sub">
                {t("services.detail.sectionsSub")}
              </p>
            </header>

            <div className="ac-shop-detail__accordion">
              <DetailCollapseSection
                sectionId="overview"
                title={t("menu.overview")}
                icon={
                  <svg viewBox="0 0 20 20" fill="none" width="18" height="18">
                    <path
                      d="M4 6h12M4 10h12M4 14h8"
                      stroke="currentColor"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                    />
                  </svg>
                }
                count={list?.feature_ids?.length ?? 0}
                isOpen={openSection === "overview"}
                onToggle={toggleSection}
                isRtl={isRtl}
              >
                {(list?.feature_ids?.length ?? 0) > 0 ? (
                  <p
                    className={`ac-shop-detail__features-heading${
                      isRtl ? " ac--font-expo" : ""
                    }`}
                  >
                    {t("services.includedHeading")}
                  </p>
                ) : null}
                <div className="ac-shop-detail__features">
                  {(list?.feature_ids ?? []).map((item: any, index: number) => (
                    <div key={item?.id ?? index} className="ac-shop-detail__feature">
                      <span className="ac-shop-detail__feature-check" aria-hidden>
                        <svg viewBox="0 0 12 10" fill="none">
                          <path
                            d="M1 5.5L4.2 8.5L11 1.5"
                            stroke="currentColor"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </span>
                      <p className="ac-shop-detail__feature-name">{item.name}</p>
                    </div>
                  ))}
                </div>
                {list?.features_description ? (
                  <div
                    className="ac-shop-detail__features-desc"
                    dangerouslySetInnerHTML={{
                      __html: list.features_description,
                    }}
                  />
                ) : (list?.feature_ids?.length ?? 0) === 0 ? (
                  <div className="ac-shop-detail__empty ac-shop-detail__empty--compact">
                    <p className="ac-shop-detail__empty-text">
                      {t("services.detail.overviewEmpty")}
                    </p>
                  </div>
                ) : null}
              </DetailCollapseSection>

              <QuestionAnswers
                question_ids={list?.question_ids}
                sectionId="questions"
                isOpen={openSection === "questions"}
                onToggle={toggleSection}
              />
              <RateAndReview
                reviewNumber={list?.reviews_count}
                rateNumber={list?.reviews_rate}
                review_ids={list?.review_ids}
                sectionId="reviews"
                isOpen={openSection === "reviews"}
                onToggle={toggleSection}
              />
              {cookies.apiKey && list?.id ? (
                <AddReview
                  id={list.id}
                  sectionId="add-review"
                  isOpen={openSection === "add-review"}
                  onToggle={toggleSection}
                  isRtl={isRtl}
                  onReviewSubmitted={
                    isService
                      ? refreshServiceDetail
                      : () =>
                          dispatch(
                            toGetDetails({ id: list.id, lang: i18n.language })
                          )
                  }
                />
              ) : null}
            </div>
          </div>
        </section>

        <Footer type="external" />
      </main>

      <ChooseVehicleModal
        open={chooseVehicleOpen}
        onClose={closeChooseVehicleModal}
        onVehicleSelected={onVehicleChosenForServiceDetail}
      />

      {lightboxIndex != null && slides[lightboxIndex] ? (
        <div
          className="ac-image-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label={t("services.detail.imagePreview")}
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setLightboxIndex(null);
          }}
        >
          <div className="ac-image-lightbox__toolbar">
            <span>{lightboxIndex + 1} / {slides.length}</span>
            <button
              type="button"
              onClick={() => setLightboxIndex(null)}
              aria-label={t("services.detail.closePreview")}
            >
              <IoClose aria-hidden />
            </button>
          </div>
          {slides.length > 1 ? (
            <button
              type="button"
              className="ac-image-lightbox__nav ac-image-lightbox__nav--prev"
              onClick={() =>
                setLightboxIndex((lightboxIndex - 1 + slides.length) % slides.length)
              }
              aria-label={t("services.detail.previousImage")}
            >
              <IoIosArrowBack aria-hidden />
            </button>
          ) : null}
          {slides[lightboxIndex].video ? (
            <video
              className="ac-image-lightbox__image"
              src={slides[lightboxIndex].src}
              controls
              autoPlay
              playsInline
            />
          ) : (
            <img
              className="ac-image-lightbox__image"
              src={slides[lightboxIndex].src}
              alt={productName ? `${productName} — ${lightboxIndex + 1}` : ""}
            />
          )}
          {slides.length > 1 ? (
            <button
              type="button"
              className="ac-image-lightbox__nav ac-image-lightbox__nav--next"
              onClick={() => setLightboxIndex((lightboxIndex + 1) % slides.length)}
              aria-label={t("services.detail.nextImage")}
            >
              <IoIosArrowForward aria-hidden />
            </button>
          ) : null}
        </div>
      ) : null}
    </>
  );
};

export default Detail;
